/**
 * 一张知识图谱只许画【一个坐标系】。
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * 2026-08-10 现场(用户:「狗记忆里的数据是不是重复很多」)
 * ══════════════════════════════════════════════════════════════════════════════
 * 认知页画出 329 个物体, 满屏成对的「行李箱 / 服务器机柜 / 显示器 / 钟」。查了
 * 狗上的 memory.db:
 *
 *     map_1786253960455   185 个实例   2026-08-09 13:40 ~ 15:11
 *     map_1786321215611   144 个实例   2026-08-10 08:25 ~ 12:03
 *     185 + 144 = 329 —— 正是页面上那个数
 *
 * 同一个办公室、两天、两张地图(08-10 早上重新建了图, 原点平移了 3~4 m)。
 * 34 个名字两张图里都有, 占掉 324 个实例中的 226 个。
 *
 * **数据没有错, 是画的时候把两个坐标系当成了一个。** 导出侧写得很清楚 ——
 * `AllInstances` 的注释就是 "every merged object instance across all maps",
 * 每行都带着 mapId; 而读取侧 `SELECT … WHERE device_id=? ORDER BY map_id, name`
 * 把它们一股脑喂给同一张力导向图, 于是:
 *   · 同一件东西在两张图里的两个实例并排出现 ⇒ 看起来像"记重复了";
 *   · 物体↔地点那条边的距离是【算】出来的, 而跨图算距离没有意义 ——
 *     两个坐标系之间差着一个没人求过的变换。
 *
 * ★ 这与本页头部第 2 条是同一条规矩的两个面:
 *   「谁也不许把两台设备看到的同一张桌子算成一个」——
 *   同样, 谁也不许把两张图里的坐标当成同一个空间。
 *   合并需要的是跨图对齐(重定位那条线), 在它做出来之前, 诚实的做法是
 *   **一次只画一张图, 并且把没画的那部分说出来**, 而不是叠在一起假装是一个。
 *
 * 本模块是纯函数, 判据见 __tests__/knowledgeMaps.test.mjs。
 */

/** 一台设备建过的一张图, 以及它在这份快照里有多少东西。 */
export interface MapScope {
  deviceId: string;
  mapId: string;
  nObjects: number;
  nPlaces: number;
  /** 该图里最近一次见到东西的时间(秒)。0 = 这张图的时间戳全都不可信。 */
  lastSeen: number;
}

/** 设备自报时间 < 2000-01-01 = 狗没有 RTC, 那个数不是时间。与本页 deviceTime 同一条线。 */
export const CLOCK_OK_AFTER = 946684800;

interface HasMap { deviceId: string; mapId: string }
interface HasSeen extends HasMap { lastSeen: number }

/**
 * 列出快照里所有 (设备, 图), 按【最近见到】降序; 时间不可信时退回 mapId 降序。
 *
 * ★ 退路要说清楚: mapId 形如 `map_<毫秒>`, 同样长度时字典序即时序 —— 这是个
 *   **约定**而不是保证, 所以只在时间戳全不可信时才用它, 而且不假装它是时间
 *   (lastSeen 照实报 0)。
 */
export function mapScopes(objects: HasSeen[], places: HasMap[]): MapScope[] {
  const by = new Map<string, MapScope>();
  const key = (d: string, m: string) => d + ' ' + m;
  const get = (deviceId: string, mapId: string): MapScope => {
    const k = key(deviceId, mapId);
    let s = by.get(k);
    if (!s) {
      s = { deviceId, mapId, nObjects: 0, nPlaces: 0, lastSeen: 0 };
      by.set(k, s);
    }
    return s;
  };
  for (const o of objects) {
    const s = get(o.deviceId, o.mapId);
    s.nObjects += 1;
    if (o.lastSeen >= CLOCK_OK_AFTER && o.lastSeen > s.lastSeen) s.lastSeen = o.lastSeen;
  }
  for (const p of places) {
    get(p.deviceId, p.mapId).nPlaces += 1;
  }
  return [...by.values()].sort((a, b) =>
    b.lastSeen - a.lastSeen || (a.mapId < b.mapId ? 1 : a.mapId > b.mapId ? -1 : 0));
}

/**
 * 每台设备取【一张】图 —— 默认是它最近有动静的那张。返回 deviceId → mapId。
 *
 * ★ 为什么按设备各取一张, 而不是全局取最新的一张: 群体视图里几台狗各在各的场地,
 *   全局取一张会让其余设备整个消失 —— 那不是"少画一点", 那是"这台狗没有知识"。
 */
export function currentMapOf(scopes: MapScope[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of scopes) {
    if (!(s.deviceId in out)) out[s.deviceId] = s.mapId;   // scopes 已按新到旧排好
  }
  return out;
}

/**
 * 按 (设备 → 图) 留下该画的行, 并把丢掉的数目【原样报出来】。
 *
 * ★ 静默截断是这个仓库反复吃过亏的东西: 少画了不说, 读图的人会把"我没画"读成
 *   "它没有"。所以返回值里必须带 dropped, 调用方必须显示它。
 */
export function scopeRows<T extends HasMap>(
  rows: T[], keep: Record<string, string>,
): { kept: T[]; dropped: number } {
  const kept = rows.filter((r) => keep[r.deviceId] === r.mapId);
  return { kept, dropped: rows.length - kept.length };
}
